/**
 * 多页账单（.pure-print-page）与屏幕预览 / PDF 共用的逐页渲染；浏览器打印走 DOM 克隆（秒开）。
 */

import { mmToPx } from '@/utils/printPageGeometry.js'
import {
  buildPrintPreviewToolbarHtml,
  buildPrintWindowHintHtml,
  PRINT_PREVIEW_TOOLBAR_CSS
} from '@/utils/printDomClone.js'

export function collectPurePrintPages (previewContentEl) {
  const region = previewContentEl?.querySelector('.print-table-region.pure-preview-pages')
  return Array.from(region?.querySelectorAll('.pure-print-page') || [])
}

export function stripPurePrintPageChrome (el) {
  if (!el?.style) return
  el.style.border = 'none'
  el.style.boxShadow = 'none'
  el.style.borderRadius = '0'
  el.style.margin = '0'
  el.style.marginBottom = '0'
}

/** 栅格化前锁定为纸张像素尺寸，避免后半段页高度漂移 */
export function lockPurePrintPageCaptureSize (el, paperWmm, paperHmm) {
  if (!el?.style) return
  const w = Math.round(mmToPx(Number(paperWmm) || 210))
  const h = Math.round(mmToPx(Number(paperHmm) || 297))
  el.style.boxSizing = 'border-box'
  el.style.width = `${w}px`
  el.style.height = `${h}px`
  el.style.minWidth = `${w}px`
  el.style.maxWidth = `${w}px`
  el.style.minHeight = `${h}px`
  el.style.maxHeight = `${h}px`
  el.style.overflow = 'hidden'
  return { w, h }
}

/**
 * 将每页 DOM 栅格化为 JPEG data URL（与 exportPrintPreviewPdf 多页逻辑一致）
 * @returns {Promise<Array<{ dataUrl: string, width: number, height: number }>>}
 */
export async function rasterizePurePrintPages (
  pageElements,
  { scale = 2, jpegQuality = 0.92, paperWmm = 210, paperHmm = 297 } = {}
) {
  if (!pageElements?.length) return []

  const [{ default: html2canvas }] = await Promise.all([import('html2canvas')])
  const locked = lockPurePrintPageCaptureSize(document.createElement('div'), paperWmm, paperHmm)
  const capW = locked.w
  const capH = locked.h

  const host = document.createElement('div')
  host.setAttribute('aria-hidden', 'true')
  host.style.cssText = [
    'position:fixed',
    'left:0',
    'top:0',
    'z-index:2147483646',
    'pointer-events:none',
    'overflow:hidden',
    'background:#fff',
    'opacity:0.015'
  ].join(';')
  document.body.appendChild(host)

  try {
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      await document.fonts.ready.catch(() => {})
    }

    const out = []
    for (const pageEl of pageElements) {
      const clone = pageEl.cloneNode(true)
      stripPurePrintPageChrome(clone)
      lockPurePrintPageCaptureSize(clone, paperWmm, paperHmm)
      host.replaceChildren(clone)
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)))

      const canvas = await html2canvas(clone, {
        scale,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: capW,
        height: capH,
        scrollX: 0,
        scrollY: 0
      })

      out.push({
        dataUrl: canvas.toDataURL('image/jpeg', jpegQuality),
        width: capW,
        height: capH
      })
    }
    return out
  } finally {
    if (host.parentNode) {
      host.parentNode.removeChild(host)
    }
  }
}

/** 统计预览页表格 tbody 行数 */
function countPrintTableBodyRows (pageEl) {
  return pageEl?.querySelectorAll('.print-table tbody tr')?.length || 0
}

/**
 * 打印克隆：清除预览 inline 定位/高度（否则浏览器打印仍按 absolute 分页，页脚被挤到下一页）。
 */
export function normalizePurePrintPageCloneForDomPrint (pageEl, { chunkIndex, totalPages, rowsPerPage } = {}) {
  stripPurePrintPageChrome(pageEl)
  if (!pageEl?.style) return

  const idx = Number(chunkIndex) || 0
  const total = Math.max(1, Number(totalPages) || 1)
  const per = Math.max(1, Number(rowsPerPage) || 1)
  const rowCount = countPrintTableBodyRows(pageEl)
  const isLast = idx === total - 1
  const isPenultimate = idx === total - 2
  if (isPenultimate && !isLast && idx > 0 && rowCount >= per - 2) {
    pageEl.classList.add('pure-print-page--print-relaxed')
  }

  pageEl.setAttribute('data-chunk-index', String(idx))
  if (isLast) pageEl.setAttribute('data-is-last', '1')

  const clearBox = (el) => {
    if (!el?.style) return
    el.style.position = ''
    el.style.top = ''
    el.style.left = ''
    el.style.right = ''
    el.style.bottom = ''
    el.style.height = ''
    el.style.minHeight = ''
    el.style.maxHeight = ''
    el.style.width = ''
    el.style.display = ''
    el.style.flex = ''
    el.style.flexDirection = ''
    el.style.overflow = ''
    el.style.marginTop = ''
  }

  clearBox(pageEl)
  clearBox(pageEl.querySelector('.pure-print-main'))
  clearBox(pageEl.querySelector('.pure-print-footer'))
}

/** 多页账单：克隆预览 DOM 为打印 HTML 片段（flex 贴底，避免页脚被打印引擎单独分页） */
export function clonePurePrintPagesHtml (previewContentEl, { rowsPerPage } = {}) {
  const pages = collectPurePrintPages(previewContentEl)
  if (!pages.length) return ''
  const wrap = document.createElement('div')
  pages.forEach((el, idx) => {
    const clone = el.cloneNode(true)
    normalizePurePrintPageCloneForDomPrint(clone, {
      chunkIndex: idx,
      totalPages: pages.length,
      rowsPerPage
    })
    wrap.appendChild(clone)
  })
  return wrap.innerHTML
}

/** 多页账单：DOM 克隆打印（秒开；flex + 固定像素页高，页脚与正文同一分页上下文） */
export function buildPureDomPrintDocumentHtml ({
  bodyHtml,
  atPageSize,
  paperWmm,
  paperHmm,
  marginTop = 10,
  marginRight = 10,
  marginBottom = 10,
  marginLeft = 10,
  footerHeightMm = 7,
  tableBorderCss = '0.75px solid #000',
  tableBorderWidthPx = 0.75,
  printBodyFf = 'inherit',
  printBodyFs = 12,
  title = '打印'
} = {}) {
  const pageTitle = title === '打印' ? '打印预览' : title
  const pageWPx = Math.round(mmToPx(Number(paperWmm) || 210))
  const pageHPx = Math.round(mmToPx(Number(paperHmm) || 297))
  const padTopPx = Math.round(mmToPx(marginTop))
  const padRightPx = Math.round(mmToPx(marginRight))
  const padBottomPx = Math.round(mmToPx(marginBottom))
  const padLeftPx = Math.round(mmToPx(marginLeft))
  // 打印页脚区比模板预留略高，吸收边框/行高舍入误差（仅打印 HTML，不改预览）
  const footerReserveMm = Number(footerHeightMm) + 4
  const footerHPx = Math.round(mmToPx(footerReserveMm))
  const innerHPx = Math.max(1, pageHPx - padTopPx - padBottomPx)
  const mainMaxHPx = Math.max(1, innerHPx - footerHPx)
  const pageSize = atPageSize || 'A4 portrait'

  return `<!DOCTYPE html>
<html lang="zh-CN">
<head>
<meta charset="utf-8" />
<title>${pageTitle}</title>
<style>
${PRINT_PREVIEW_TOOLBAR_CSS}
@page { size: ${pageSize}; margin: 0; }
html, body { margin: 0; padding: 0; background: #fff; font-family: ${printBodyFf}; font-size: ${printBodyFs}px; color: #111; }
.pure-print-page {
  box-sizing: border-box !important;
  display: flex !important;
  flex-direction: column !important;
  width: ${pageWPx}px !important;
  height: ${pageHPx}px !important;
  min-height: ${pageHPx}px !important;
  max-height: ${pageHPx}px !important;
  padding: ${padTopPx}px ${padRightPx}px ${padBottomPx}px ${padLeftPx}px !important;
  margin: 0 !important;
  position: relative !important;
  overflow: hidden !important;
  page-break-inside: avoid !important;
  break-inside: avoid !important;
  background: #fff !important;
}
.pure-print-main {
  position: static !important;
  flex: 0 1 auto !important;
  max-height: ${mainMaxHPx}px !important;
  min-height: 0 !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
  box-sizing: border-box !important;
}
.pure-print-page .print-table {
  flex: 1 1 auto !important;
  min-height: 0 !important;
  max-height: 100% !important;
  overflow: hidden !important;
  padding-bottom: ${tableBorderWidthPx}px !important;
  box-sizing: border-box !important;
}
.pure-print-page--print-relaxed .pure-print-main {
  max-height: ${Math.max(1, mainMaxHPx - 8)}px !important;
}
.pure-print-footer {
  position: static !important;
  flex: 0 0 ${footerHPx}px !important;
  flex-shrink: 0 !important;
  margin-top: auto !important;
  min-height: 0 !important;
  overflow: hidden !important;
  box-sizing: border-box !important;
  padding: 0 10px !important;
  border-top: 1px solid #eee !important;
  line-height: 1.2 !important;
  white-space: normal !important;
  word-break: break-word !important;
  background: #fff !important;
}
.pure-print-footer .print-footer-line {
  width: 100%;
  white-space: normal;
  word-break: break-word;
  overflow: hidden;
  line-height: 1.2;
}
.pure-print-footer .print-footer-line p,
.pure-print-footer .print-footer-line div,
.pure-print-footer .print-footer-line span,
.pure-print-footer .page-number,
.pure-print-footer .total-pages {
  display: inline !important;
  white-space: inherit !important;
  margin: 0 !important;
  padding: 0 !important;
}
.print-table table { width: 100%; border-collapse: collapse; table-layout: fixed; }
.print-table th, .print-table td { box-sizing: border-box; border: ${tableBorderCss}; }
.print-table thead th { font-weight: 700; background: #fff !important; }
.print-hint {
  display: none;
}
@media print {
  html, body { margin: 0; padding: 0; }
  .pure-print-page {
    width: ${pageWPx}px !important;
    height: ${pageHPx}px !important;
    min-height: ${pageHPx}px !important;
    max-height: ${pageHPx}px !important;
  }
}
</style>
</head>
<body>
${buildPrintPreviewToolbarHtml()}
${buildPrintWindowHintHtml()}
<div class="erp-print-doc">
${bodyHtml || ''}
</div>
</body>
</html>`
}

/** 栅格化完成前写入打印窗口，避免空白页被关闭 */
export function buildPrintPreparingDocumentHtml ({ title = '打印' } = {}) {
  return `<!DOCTYPE html>
<html lang="zh-CN">
<head>
<meta charset="utf-8" />
<title>${title}</title>
<style>
html, body {
  margin: 0;
  height: 100%;
  font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
  color: #444;
  background: #fff;
}
body {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  padding: 24px;
  box-sizing: border-box;
}
p { margin: 0; }
.hint { font-size: 14px; color: #999; }
</style>
</head>
<body>
<p>正在准备打印，请稍候…</p>
<p class="hint">正在生成 PDF，请勿关闭此窗口</p>
</body>
</html>`
}

/** 由栅格化页图构建独立打印 HTML（固定像素，一页一图，与导出 PDF 同源） */
export function buildRasterPrintDocumentHtml ({
  pageImages,
  atPageSize,
  paperWmm,
  paperHmm,
  title = '打印'
} = {}) {
  const list = Array.isArray(pageImages) ? pageImages : []
  const pageWPx = list[0]?.width || Math.round(mmToPx(Number(paperWmm) || 210))
  const pageHPx = list[0]?.height || Math.round(mmToPx(Number(paperHmm) || 297))
  const pageSize = atPageSize || 'A4 portrait'
  const pagesHtml = list
    .map(({ dataUrl }, idx) => {
      const breakCls = idx < list.length - 1 ? ' print-raster-page--break' : ''
      return `<div class="print-raster-page${breakCls}"><img src="${dataUrl}" width="${pageWPx}" height="${pageHPx}" alt="" /></div>`
    })
    .join('\n')

  return `<!DOCTYPE html>
<html lang="zh-CN">
<head>
<meta charset="utf-8" />
<title>${title}</title>
<style>
@page { size: ${pageSize}; margin: 0; }
html, body {
  margin: 0;
  padding: 0;
  background: #fff;
}
.print-raster-page {
  display: block;
  width: ${pageWPx}px;
  height: ${pageHPx}px;
  margin: 0;
  padding: 0;
  overflow: hidden;
  box-sizing: border-box;
  break-inside: avoid;
  page-break-inside: avoid;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.print-raster-page--break {
  break-after: page;
  page-break-after: always;
}
.print-raster-page img {
  display: block;
  width: ${pageWPx}px;
  height: ${pageHPx}px;
  margin: 0;
  padding: 0;
  border: 0;
}
@media print {
  html, body { margin: 0; padding: 0; }
}
</style>
</head>
<body>${pagesHtml}</body>
</html>`
}

/**
 * 多页账单浏览器打印：与导出 PDF 相同栅格化，写入固定像素 HTML（避免 DOM/mm 分页）。
 */
export async function prepareMultiPageStatementPrintHtml ({
  previewContentEl,
  templateConfig,
  rasterScale = 1.5
} = {}) {
  const { mergeTemplatePage } = await import('@/utils/exportDeliveryOrderPrintExcel.js')
  const { getMultiPageRasterizeOptions } = await import('@/utils/exportPrintPreviewPdf.js')
  const { buildAtPageSizeCss, getPhysicalPaperMm } = await import('@/utils/printPageGeometry.js')
  const pageCfg = mergeTemplatePage(templateConfig?.page)
  const paperMm = getPhysicalPaperMm(pageCfg)
  const pageElements = collectPurePrintPages(previewContentEl)
  if (!pageElements.length) return null
  const rasterOpts = getMultiPageRasterizeOptions(templateConfig)
  const pageImages = await rasterizePurePrintPages(pageElements, {
    scale: rasterScale,
    jpegQuality: rasterOpts.jpegQuality,
    paperWmm: paperMm.w,
    paperHmm: paperMm.h
  })
  return buildRasterPrintDocumentHtml({
    pageImages,
    atPageSize: buildAtPageSizeCss(pageCfg),
    paperWmm: paperMm.w,
    paperHmm: paperMm.h,
    title: '打印'
  })
}

/**
 * 在已打开的打印窗口加载 PDF Blob 并触发 print()。
 * 直接打开 PDF（与导出文件相同），避免 HTML+mm 高度在浏览器打印时再切页。
 */
export function loadPdfBlobInPrintWindow (printWindow, blob, { delayMs = 1500 } = {}) {
  if (!printWindow || printWindow.closed || !blob) {
    return Promise.reject(new Error('打印窗口不可用'))
  }

  const url = URL.createObjectURL(blob)

  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      if (printWindow.closed) {
        URL.revokeObjectURL(url)
        reject(new Error('打印窗口已关闭'))
        return
      }
      try {
        printWindow.focus()
        printWindow.print()
        setTimeout(() => URL.revokeObjectURL(url), 120000)
        resolve()
      } catch (err) {
        URL.revokeObjectURL(url)
        reject(err)
      }
    }, delayMs)

    printWindow.addEventListener('beforeunload', () => clearTimeout(timer), { once: true })
    printWindow.location.replace(url)
  })
}
